import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { useTicketDetail } from './TicketDetail';
import { QueryView } from '../../ui/feedback';
import { Markdown } from '../../ui/Markdown';
import { StatusBadge, humanize } from '../../ui/Badge';

type Selection = { projectKey: string; number: number };
const QuickView = createContext<((ticket: Selection) => void) | null>(null);

/** Keep board/search mounted, including their filters, loaded pages and scroll containers. */
export function TicketQuickViewProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [selection, setSelection] = useState<(Selection & { locationKey: string }) | null>(null);
  const qc = useQueryClient();
  const close = useCallback(() => {
    setSelection(null);
    // Existing workspace mutations invalidate ticket data; refresh mounted list summaries too.
    for (const queryKey of [['board'], ['board-more'], ['search'], ['project']]) {
      void qc.invalidateQueries({ queryKey });
    }
  }, [qc]);
  const open = useCallback(
    (ticket: Selection) => {
      setSelection({ ...ticket, locationKey: location.key });
    },
    [location.key],
  );
  useEffect(() => {
    if (selection && selection.locationKey !== location.key) close();
  }, [location.key, selection, close]);
  const visible = selection?.locationKey === location.key ? selection : null;
  const href = visible ? `/projects/${visible.projectKey}/tickets/${visible.number}` : '';
  return (
    <QuickView.Provider value={open}>
      {children}
      {visible && (
        <Modal
          title={`${visible.projectKey}-${visible.number}`}
          onClose={close}
          className="ticket-preview-panel"
          placement="right"
        >
          <div className="row between quick-view-controls">
            <Link className="btn btn-secondary sm" to={href}>
              Open full page
            </Link>
            <Button size="sm" variant="secondary" onClick={close} data-autofocus>
              Close
            </Button>
          </div>
          <TicketPreview projectKey={visible.projectKey} number={visible.number} />
        </Modal>
      )}
    </QuickView.Provider>
  );
}

/** Real href keeps copy-link, middle-click and modified-click browser behavior. */
export function TicketLink({
  projectKey,
  number,
  onClick,
  ...props
}: Omit<ComponentProps<typeof Link>, 'to'> & Selection) {
  const open = useContext(QuickView);
  return (
    <Link
      {...props}
      to={`/projects/${projectKey}/tickets/${number}`}
      onClick={(event) => {
        onClick?.(event);
        if (
          !open ||
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          (props.target && props.target !== '_self')
        )
          return;
        event.preventDefault();
        open({ projectKey, number });
      }}
    />
  );
}

/** Compact reading preview; full editing and workflow controls live on the canonical page. */
function TicketPreview({ projectKey, number }: Selection) {
  const q = useTicketDetail(projectKey, number);
  return (
    <QueryView query={q}>
      {(t) => (
        <div className="stack lg">
          <h3 className="ticket-preview-title">{t.title}</h3>
          <div className="row">
            <StatusBadge status={t.status} resolution={t.resolution} />
            <span className="small muted">{humanize(t.priority)} priority</span>
          </div>
          <section aria-label="Description preview" className="stack sm">
            <h3 className="title-sm">Description</h3>
            {t.description.trim() ? (
              <Markdown>
                {t.description.length > 1200 ? t.description.slice(0, 1200) + '\n\n…' : t.description}
              </Markdown>
            ) : (
              <span className="small muted">No description provided.</span>
            )}
          </section>
          <section aria-label="Acceptance criteria preview" className="stack sm">
            <h3 className="title-sm">Acceptance criteria ({t.acceptanceCriteria.length})</h3>
            {t.acceptanceCriteria.length ? (
              <ol className="stack sm">
                {t.acceptanceCriteria.slice(0, 3).map((c, i) => (
                  <li key={i}>
                    <Markdown>{c.length > 300 ? c.slice(0, 300) + '…' : c}</Markdown>
                  </li>
                ))}
              </ol>
            ) : (
              <span className="small muted">No acceptance criteria provided.</span>
            )}
            {t.acceptanceCriteria.length > 3 && (
              <span className="small muted">{t.acceptanceCriteria.length - 3} more on the full page</span>
            )}
          </section>
          <p className="small muted">
            {t.commentCount} discussion comment{t.commentCount === 1 ? '' : 's'} · Open the full page for all
            details and actions.
          </p>
        </div>
      )}
    </QueryView>
  );
}
